/**
 * Add All Domains to Cart Button
 * P20250925-106 — Atomic, idempotent cart operation
 */

import React, { useState } from 'react';
import { ShoppingCart, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { addToCart, isInCart } from '@/lib/cartStore';
import { fireEvent } from '@/lib/analytics';
import { toast } from '@/hooks/use-toast';
import type { BundleDomain } from '@/lib/bundles/types';

interface AddAllToCartButtonProps {
  domains: BundleDomain[];
  bundleSlug: string;
  bundleTitle: string;
  location?: 'index' | 'detail';
  variant?: 'default' | 'outline' | 'secondary';
  size?: 'default' | 'sm' | 'lg';
  className?: string;
}

export default function AddAllToCartButton({
  domains,
  bundleSlug,
  bundleTitle,
  location = 'detail',
  variant = 'default',
  size = 'default',
  className,
}: AddAllToCartButtonProps) {
  const [isAdding, setIsAdding] = useState(false);

  // Filter to available domains with prices
  const availableDomains = domains.filter(
    (d) => d.status === 'available' && d.binPrice > 0 && d.flags.bin,
  );

  // Check how many are already in cart
  const domainsInCart = availableDomains.filter((d) => isInCart(d.name));
  const allInCart = availableDomains.length > 0 && domainsInCart.length === availableDomains.length;

  const handleAddAllToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isAdding || availableDomains.length === 0) return;

    setIsAdding(true);

    try {
      let addedCount = 0;
      let skippedCount = 0;

      // Add each domain atomically
      for (const domain of availableDomains) {
        const added = addToCart({
          domain: domain.name,
          tld: domain.tld,
          binPrice: domain.binPrice,
        });

        if (added) {
          addedCount++;
        } else {
          skippedCount++;
        }
      }

      // Track analytics (no PII)
      fireEvent('bundle_add_all_to_cart', {
        bundle_slug: bundleSlug,
        bundle_title: bundleTitle,
        total_domains: availableDomains.length,
        added_count: addedCount,
        skipped_count: skippedCount,
        location,
      });

      // Show toast feedback
      if (addedCount > 0) {
        toast({
          title: 'Added to cart',
          description:
            skippedCount > 0
              ? `${addedCount} domains added. ${skippedCount} were already in your cart.`
              : `All ${addedCount} domains from ${bundleTitle} added to cart.`,
        });
      } else {
        toast({
          title: 'Already in cart',
          description: `All domains from ${bundleTitle} are already in your cart.`,
          variant: 'default',
        });
      }
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      toast({
        title: 'Error',
        description: 'Failed to add bundle to cart. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  if (availableDomains.length === 0) {
    return null; // No available domains
  }

  return (
    <Button
      variant={allInCart ? 'outline' : variant}
      size={size}
      onClick={handleAddAllToCart}
      disabled={isAdding}
      className={className}
      aria-label={
        allInCart
          ? `All domains from ${bundleTitle} are in cart`
          : `Add all ${availableDomains.length} domains from ${bundleTitle} to cart`
      }
    >
      {isAdding ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Adding {availableDomains.length} domains...
        </>
      ) : allInCart ? (
        <>
          <Check className="w-4 h-4 mr-2" />
          All in Cart
        </>
      ) : (
        <>
          <ShoppingCart className="w-4 h-4 mr-2" />
          Add All to Cart ({availableDomains.length})
        </>
      )}
    </Button>
  );
}
